import blogAutoArticles from './blogAuto.json';

export const blogCategories = [
  { id: 'A', name: '執事の基礎知識', slug: 'basics' },
  { id: 'B', name: '執事のキャリア', slug: 'career' },
  { id: 'C', name: '作法・マナー実践', slug: 'manners' },
  { id: 'D', name: 'アカデミー便り', slug: 'academy' },
  { id: 'E', name: '業界・海外事情', slug: 'industry' },
];

export interface BlogArticle {
  slug: string;
  title: string;
  category: string;
  categoryId: string;
  date: string;
  updated: string;
  author: string;
  authorRole: string;
  keywords: string[];
  excerpt: string;
  definitionText: string;
  imageSeq: string;
  imageUrl: string;
  toc: { id: string; text: string }[];
  body: { type: 'h2' | 'h3' | 'p' | 'list' | 'image'; content: string; items?: string[] }[];
  faq: { question: string; answer: string }[];
  relatedSlugs: string[];
  ctaType: 'courses' | 'seminar' | 'corporate' | 'brochure';
}

const manualBlogArticles: BlogArticle[] = [
  {
    slug: 'butler-difference',
    title: '執事とバトラーの違いとは？語源と現代日本での使い分けを講師が解説',
    category: '執事の基礎知識',
    categoryId: 'A',
    date: '2026-06-13',
    updated: '2026-06-13',
    author: '日本執事アカデミー講師',
    authorRole: '主任講師（全日本執事協会認定）',
    keywords: ['執事', 'バトラー', '違い', '語源', '日本'],
    excerpt: '「執事」と「バトラー」は本来同じ職業を指す言葉ですが、日本では使われる文脈に微妙な違いがあります。語源から現代の使い分け、仕事内容までを講師がわかりやすく解説します。',
    definitionText: '「執事」と「バトラー（Butler）」は本来同じ職業を指す言葉で、執事は日本語、バトラーは英語の呼称です。ただし現代の日本では、文脈によって微妙に使い分けられる場面があります。',
    imageSeq: 'blog-butler-difference',
    imageUrl: '/images/generated/blog-butler-difference-01.jpg',
    toc: [
      { id: 'same-or-different', text: '執事とバトラーは同じ？それとも違う？' },
      { id: 'butler-etymology', text: 'バトラー（Butler）の語源は？' },
      { id: 'shitsuji-origin', text: '日本語の「執事」はどこから来たのか？' },
      { id: 'modern-work', text: '現代日本での仕事内容は？' },
      { id: 'how-to-become', text: '執事・バトラーになるにはどうすればいい？' },
      { id: 'summary', text: 'まとめ' },
    ],
    body: [
      {
        type: 'h2',
        content: '執事とバトラーは同じ？それとも違う？',
      },
      {
        type: 'p',
        content: '結論から言えば、両者は同一の職業を指します。英語のButlerを日本語に訳した言葉が「執事」です。したがって「執事とバトラーの違い」を厳密に定義する辞書的な区別はありません。一方で、日本のサービス業界では次のような使い分けの傾向が見られます。',
      },
      {
        type: 'p',
        content: '「執事」は、個人のお屋敷や家庭に仕えて家政全般を取り仕切る伝統的な姿を想起させる言葉として使われることが多く、「バトラー」は、ラグジュアリーホテルのバトラーサービスなど、ホスピタリティ業界の専門職種名として使われる傾向があります。つまり違いは職業の中身ではなく、使われる場面の違いです。',
      },
      {
        type: 'h2',
        content: 'バトラー（Butler）の語源は？',
      },
      {
        type: 'p',
        content: 'Butlerの語源は、古フランス語のbouteillier（ワインの瓶=bouteilleを管理する者）に遡るとされます。中世ヨーロッパの貴族の館で酒類の管理を任された使用人が、やがて家政全体を統括する筆頭使用人へと役割を広げ、現在のバトラー像が形づくられました。英国貴族社会では、バトラーは使用人たちを束ねる存在として、テーブルサービス、来客対応、銀器やワインセラーの管理、他の使用人の統率までを担ってきました。家内使用人の最上位には家令（ハウス・スチュワード）が置かれることもありましたが、家令を置いたのは非常に大きな邸宅にかぎられ、多くの邸宅ではバトラーが実質的な長として人事や経理までを引き受けていました。',
      },
      {
        type: 'h2',
        content: '日本語の「執事」はどこから来たのか？',
      },
      {
        type: 'p',
        content: '日本語の「執事」は、もともと「事を執り行う者」を意味し、平安期の貴族の家政機関や寺社の役職名として古くから存在した言葉です。明治期以降、西洋のButlerの訳語として当てられ、現在の「主人に仕える家政の責任者」という意味で定着しました。つまり「執事」という言葉自体は、日本に古くからあった役職名が西洋のバトラー像と結びついたものです。',
      },
      {
        type: 'h2',
        content: '現代日本での仕事内容は？',
      },
      {
        type: 'p',
        content: '現代の執事・バトラーの業務は、伝統的な家事の枠を超えて広がっています。代表的なものは、食事とテーブルサービス（セッティング、ティー・ワインの提供）、衣類・靴の管理（アイロンがけ、シューケア）、来客・ゲスト対応、スケジュールと家政の管理、イベントや会食の運営、緊急時の対応です。ホテルのバトラーサービスでは、客室での専属対応（荷ほどき、ご要望の先回り）が中心になります。共通するのは、ご要望を言われる前に察して動く「先回りのホスピタリティ」です。',
      },
      {
        type: 'h2',
        content: '執事・バトラーになるにはどうすればいい？',
      },
      {
        type: 'p',
        content: '海外には執事専門の養成校があり、オランダやイギリスなどで全寮制・短期集中型の教育が行われています。日本では、日本執事アカデミー（一般社団法人全日本執事協会運営・東京お台場）のように、働きながら土日に通学して体系的に学べる環境も生まれています。基礎の所作からテーブルサービス、危機対応までを段階的に学び、修了時には協会認定資格（認定アソシエイト／プロフェッショナル／マスターバトラー ※当協会独自の民間資格）の取得を目指せます。',
      },
      {
        type: 'h2',
        content: 'まとめ',
      },
      {
        type: 'list',
        content: '',
        items: [
          '執事とバトラーは本来同じ職業（日本語と英語の違い）',
          '日本では「執事＝個人邸宅」「バトラー＝ホテル等の職種名」というニュアンスの使い分け傾向がある',
          '中身はどちらも「先回りのホスピタリティ」を体現する家政・接遇の専門職である',
        ],
      },
    ],
    faq: [
      {
        question: '執事とバトラーで資格は違いますか？',
        answer: '職業として国家資格はなく、いずれも民間の教育機関や協会の認定資格が中心です。当アカデミーでは修了実技試験の合格者に協会認定資格を授与しています。',
      },
      {
        question: '女性でも執事・バトラーになれますか？',
        answer: 'なれます。ホテルや個人邸宅で女性バトラーの活躍は広がっており、当アカデミーも性別不問で受講いただけます。',
      },
      {
        question: '執事とコンシェルジュの違いは？',
        answer: 'コンシェルジュは主に施設（ホテル等）の利用者全般に案内・手配を行う職種、執事・バトラーは特定の主人やゲストに専属で仕える点が異なります。詳しくは別記事で解説予定です。',
      },
    ],
    relatedSlugs: ['butler-career-path', 'butler-job-description', 'butler-concierge-difference'],
    ctaType: 'seminar',
  },
  {
    slug: 'butler-career-path',
    title: '執事になるには？必要なスキル・資格・学び方を養成校の講師が解説',
    category: '執事のキャリア',
    categoryId: 'B',
    date: '2026-06-13',
    updated: '2026-06-13',
    author: '日本執事アカデミー講師',
    authorRole: '主任講師（全日本執事協会認定）',
    keywords: ['執事になるには', 'スキル', '資格', '学び方', 'キャリア'],
    excerpt: '「執事になりたい」と思ったら、まず何から始めればよいのでしょうか。必要なスキル、役立つ資格、学び方の選択肢、キャリアの入り口まで、現役講師が体系的に解説します。',
    definitionText: '執事になるために国家資格や学歴は必要ありません。必要なのは「体系的に学んだ技能」と「信頼に足る人柄」であり、養成機関で基礎から段階的に身につけるのが最短の道です。',
    imageSeq: 'blog-career-path',
    imageUrl: '/images/generated/blog-career-path-02.jpg',
    toc: [
      { id: 'qualification-needed', text: '執事になるのに資格や学歴は必要？' },
      { id: 'five-skills', text: '執事に求められる5つのスキルとは？' },
      { id: 'learning-options', text: '執事の学び方には何がある？' },
      { id: 'age-and-experience', text: '何歳からでも目指せる？未経験でも大丈夫？' },
      { id: 'career-path', text: '卒業後のキャリアの入り口は？' },
      { id: 'summary', text: 'まとめ' },
    ],
    body: [
      {
        type: 'h2',
        content: '執事になるのに資格や学歴は必要？',
      },
      {
        type: 'p',
        content: '執事という職業に法律上の資格要件はありません。学歴・年齢・性別も問われないのが一般的です。ただし、雇用主は「何を学び、何ができるのか」を必ず見ます。テーブルサービスや衣類管理といった技能の証明、そして守秘・誠実さといった人柄の信頼が、事実上の採用条件になります。協会や養成校が発行する認定資格・修了証は、その証明手段として機能します。',
      },
      {
        type: 'h2',
        content: '執事に求められる5つのスキルとは？',
      },
      {
        type: 'p',
        content: '第一にサービス技能（テーブルセッティング、ティー・ワインサービス、衣類・靴のケア）。第二に先回りの観察力（言われる前に察する）。第三にコミュニケーション（品格ある言葉遣い、距離感、守秘）。第四に管理能力（家政・スケジュール・他スタッフの統率）。第五に危機対応力（体調不良、トラブル、想定外への冷静な対処）。これらは才能ではなく、訓練で身につく技術です。当アカデミーのカリキュラムもこの5領域を段階的に設計しています。',
      },
      {
        type: 'h2',
        content: '執事の学び方には何がある？',
      },
      {
        type: 'p',
        content: '選択肢は大きく3つあります。',
      },
      {
        type: 'list',
        content: '',
        items: [
          '海外留学：オランダやイギリスには全寮制・短期集中型の名門校があり、本場の環境で学べる一方、8週間前後の離職と渡航、200万円規模の費用（生活費込）が必要になります',
          '現場からの叩き上げ：ホテルや高級サービスの現場で経験を積む道で、時間はかかりますが実務に強くなります',
          '国内の養成機関：日本執事アカデミー（東京・お台場）のように、土日通学・通年開催で、仕事を続けながら48〜96時間の体系的な教育を受けられる環境です。1ヶ月のエッセンシャル（基礎）から、3ヶ月のマスター（管理・危機対応まで）まで、目標に応じて選べます',
        ],
      },
      {
        type: 'h2',
        content: '何歳からでも目指せる？未経験でも大丈夫？',
      },
      {
        type: 'p',
        content: '執事は経験の蓄積が価値になる職業であり、社会人経験そのものが武器になります。接客業出身者はもちろん、営業・秘書・医療・士業など「人に仕える誠実さ」を培った経歴は高く評価されます。当アカデミーの受講資格も18歳以上・経験不問です。年齢を理由に諦める必要はありません。',
      },
      {
        type: 'h2',
        content: '卒業後のキャリアの入り口は？',
      },
      {
        type: 'p',
        content: '主な進路は、個人邸宅・ファミリーオフィスの執事、ラグジュアリーホテルのバトラー、高級レジデンスのコンシェルジュ、富裕層向けサービス企業、そして現在の職場での接遇責任者です。日本では富裕層世帯の増加（野村総合研究所の2025年発表では富裕層・超富裕層は合計165.3万世帯と過去最多）を背景に、上質なパーソナルサービスの担い手への需要が高まっています。当アカデミーでは、修了生向けの求人情報提供の仕組みを準備しています。',
      },
      {
        type: 'h2',
        content: 'まとめ',
      },
      {
        type: 'list',
        content: '',
        items: [
          '執事に国家資格は不要だが、技能の証明（認定資格・修了証）が採用の鍵になる',
          'スキルは5領域（サービス技能・観察力・コミュニケーション・管理・危機対応）で、訓練で習得できる',
          '学び方は海外留学・現場経験・国内養成校の3択で、離職せず体系的に学ぶなら土日通学型が現実的',
        ],
      },
    ],
    faq: [
      {
        question: '執事になるまでどれくらいの期間がかかりますか？',
        answer: '基礎だけなら1ヶ月（48時間）で土台を作れます。管理職レベルの総合力を目指す場合、当アカデミーではマスターコース（3ヶ月・96時間）を用意しています。',
      },
      {
        question: '英語ができないと執事になれませんか？',
        answer: '国内の個人邸宅では日本語のみで務まる職場も多くあります。外資系ホテルや海外ゲスト対応を目指す場合は、サービス英語を段階的に学ぶことをお勧めします。',
      },
      {
        question: 'まず何から始めればいいですか？',
        answer: '1日完結の体験講座で執事の所作とティーサービスを実際に体験してみてください。参加費（33,000円税込）は本科お申込み時に全額充当されます。',
      },
    ],
    relatedSlugs: ['butler-difference', 'butler-job-description', 'butler-qualification-guide'],
    ctaType: 'seminar',
  },
  {
    slug: 'butler-job-description',
    title: '執事の仕事内容とは？1日の流れと求められる能力',
    category: '執事の基礎知識',
    categoryId: 'A',
    date: '2026-05-15',
    updated: '2026-06-13',
    author: '日本執事アカデミー講師',
    authorRole: '主任講師（全日本執事協会認定）',
    keywords: ['執事', '仕事内容', '1日の流れ', '能力'],
    excerpt: '執事の具体的な仕事内容を、個人邸宅とホテルの両方の現場から解説します。朝の準備から夜の締めまで、1日の流れに沿って紹介。',
    definitionText: '執事の仕事は、朝の邸宅点検から夜の施錠確認まで、1日を通して依頼主の生活の質を支える総合的なホスピタリティ業務です。',
    imageSeq: 'blog-job-description',
    imageUrl: '/images/generated/blog-job-description-01.jpg',
    toc: [
      { id: 'morning', text: '個人邸宅の執事：朝のルーティン' },
      { id: 'daytime', text: '日中の業務と来客対応' },
      { id: 'evening', text: 'ディナーサービスと一日の締め' },
      { id: 'hotel', text: 'ホテルバトラーの1日' },
    ],
    body: [
      {
        type: 'h2',
        content: '個人邸宅の執事：朝のルーティン',
      },
      {
        type: 'p',
        content: '個人邸宅の執事の1日は早朝から始まります。家族が起床する前に各居室の換気と温度確認、朝食の準備状況のチェック、当日のスケジュール確認を行います。新聞や郵便物の仕分けも重要な朝の業務です。',
      },
      {
        type: 'p',
        content: '依頼主が起床されたら、朝食のサービス、当日の予定の確認、必要な衣服の準備を整えます。この時間帯は依頼主と直接コミュニケーションをとる貴重な機会であり、表情や口調からその日の体調や気分を察する観察力が求められます。',
      },
      {
        type: 'h2',
        content: '日中の業務と来客対応',
      },
      {
        type: 'p',
        content: '日中は屋敷全体の管理業務が中心です。使用人の采配、消耗品の発注、修繕の手配、庭園管理の確認、車両の整備確認など、多岐にわたる業務を滞りなく進めます。来客があれば応接室への案内、茶菓の提供も執事の役割です。',
      },
      {
        type: 'h2',
        content: 'ディナーサービスと一日の締め',
      },
      {
        type: 'p',
        content: '夜はディナーサービスがメインです。テーブルセッティング、ワインの温度管理、料理の提供タイミング、すべてに細心の注意を払います。ゲストがある場合はさらに気を配り、会話の流れを妨げない絶妙なタイミングでのサービスが求められます。',
      },
    ],
    faq: [
      {
        question: '執事の仕事で一番大切な能力は何ですか？',
        answer: '「観察力」と「先回り力」です。依頼主が言葉にしなくても、表情や仕草から必要なことを察知し、求められる前に行動できることが、優れた執事の条件です。',
      },
    ],
    relatedSlugs: ['butler-difference', 'butler-career-path'],
    ctaType: 'brochure',
  },
  {
    slug: 'butler-qualification-guide',
    title: '執事に資格は必要？民間資格と学びの選択肢',
    category: '執事のキャリア',
    categoryId: 'B',
    date: '2026-04-20',
    updated: '2026-06-13',
    author: '日本執事アカデミー講師',
    authorRole: '主任講師（全日本執事協会認定）',
    keywords: ['執事', '資格', '民間資格', '学び'],
    excerpt: '執事になるために資格は本当に必要なのでしょうか。国内外の執事資格を比較し、キャリア形成にどう活かすかを解説します。',
    definitionText: '執事に必須の国家資格はありませんが、専門養成機関の修了証や民間資格は、就職時の客観的な実力証明として重要な役割を果たします。',
    imageSeq: 'blog-qualification',
    imageUrl: '/images/generated/blog-qualification-01.jpg',
    toc: [
      { id: 'overview', text: '執事と資格の関係' },
      { id: 'domestic', text: '国内の執事資格：全日本執事協会の認定制度' },
      { id: 'overseas', text: '海外の執事養成機関と修了証' },
    ],
    body: [
      {
        type: 'h2',
        content: '執事と資格の関係',
      },
      {
        type: 'p',
        content: '結論から言えば、執事として働くために法的に必須の資格はありません。医師免許や弁護士資格のような国家資格は存在せず、無資格でも執事を名乗ること自体は可能です。しかし、実務の現場では「どこで何を学んだか」が厳しく問われます。',
      },
      {
        type: 'h2',
        content: '国内の執事資格：全日本執事協会の認定制度',
      },
      {
        type: 'p',
        content: '全日本執事協会では、修了実技試験の合格者に対し3段階の認定資格を授与しています。認定アソシエイトバトラー（エッセンシャルコース修了）、認定プロフェッショナルバトラー（アドバンスコース修了）、認定マスターバトラー（マスターコース修了）です。',
      },
    ],
    faq: [
      {
        question: '資格がなくても執事として就職できますか？',
        answer: '法的には可能です。しかし、高級ホテルや富裕層向けサービスの求人では、専門養成機関の修了証や認定資格が選考の重要な判断材料となります。',
      },
    ],
    relatedSlugs: ['butler-career-path', 'butler-schools-worldwide'],
    ctaType: 'courses',
  },
  {
    slug: 'tea-brewing-pro',
    title: 'プロが教える紅茶の淹れ方：ゴールデンルールの実践',
    category: '作法・マナー実践',
    categoryId: 'C',
    date: '2026-04-05',
    updated: '2026-06-13',
    author: '日本執事アカデミー講師',
    authorRole: '主任講師（全日本執事協会認定）',
    keywords: ['紅茶', '淹れ方', 'プロ', 'ゴールデンルール'],
    excerpt: '執事が実践する紅茶のゴールデンルールを詳しく解説。茶葉の選び方からポットの温め方、抽出時間の管理まで。',
    definitionText: '紅茶のゴールデンルールとは、茶葉の量・お湯の温度・抽出時間・茶器の温めの4要素を最適化し、最高の一杯を淹れるための原則です。',
    imageSeq: 'blog-tea-brewing',
    imageUrl: '/images/generated/blog-tea-brewing-01.jpg',
    toc: [
      { id: 'golden', text: 'ゴールデンルールとは？4つの要素' },
      { id: 'steps', text: '実践：紅茶の淹れ方ステップバイステップ' },
      { id: 'common', text: 'やりがちな失敗とその対策' },
    ],
    body: [
      {
        type: 'h2',
        content: 'ゴールデンルールとは？4つの要素',
      },
      {
        type: 'p',
        content: '英国式の紅茶サービスで最も重視されるのが「ゴールデンルール」です。これは、(1)良質な茶葉を適量使うこと（1杯につきティースプーン山盛り1杯）、(2)沸騰直後の熱湯を使うこと、(3)適切な時間（3〜5分）をかけて抽出すること、(4)ポットとカップを事前に温めておくこと、の4つから成ります。',
      },
      {
        type: 'h2',
        content: '実践：紅茶の淹れ方ステップバイステップ',
      },
      {
        type: 'p',
        content: 'まずやかんで水を沸騰させます。同時にポットとカップに熱湯を注いで温めておきます。ポットが温まったらお湯を捨て、茶葉を入れ、沸騰直後の熱湯を注ぎます。フタをして3〜5分蒸らしたら、茶こしで注ぎます。カップも温めておいたお湯を捨ててから注ぐのがポイントです。',
      },
    ],
    faq: [
      {
        question: '紅茶に合うお菓子は何ですか？',
        answer: 'スコーンが定番ですが、ショートブレッドやフィンガーサンドイッチと合わせるアフタヌーンティースタイルが執事のサービスでは一般的です。',
      },
    ],
    relatedSlugs: ['butler-difference', 'afternoon-tea-manners'],
    ctaType: 'seminar',
  },
  {
    slug: 'butler-concierge-difference',
    title: '執事とコンシェルジュの違いを正しく理解する',
    category: '執事の基礎知識',
    categoryId: 'A',
    date: '2026-03-22',
    updated: '2026-06-13',
    author: '日本執事アカデミー講師',
    authorRole: '主任講師（全日本執事協会認定）',
    keywords: ['コンシェルジュ', '執事', '違い'],
    excerpt: 'よく混同される執事とコンシェルジュ。その役割の違い、必要なスキル、活躍の場を明確に区別して解説します。',
    definitionText: '執事（バトラー）は個人に専属して継続的な生活サポートを提供し、コンシェルジュは不特定多数の顧客に対しスポットの要望対応を行う、という根本的な違いがあります。',
    imageSeq: 'blog-concierge-diff',
    imageUrl: '/images/generated/blog-concierge-diff-01.jpg',
    toc: [
      { id: 'roles', text: '役割の根本的な違い' },
      { id: 'skills', text: '求められるスキルの違い' },
      { id: 'career', text: 'キャリアパスの比較' },
    ],
    body: [
      {
        type: 'h2',
        content: '役割の根本的な違い',
      },
      {
        type: 'p',
        content: '最も大きな違いは「専属か否か」です。執事は1人の依頼主または1つの家族に専属し、日々の生活全体をサポートします。朝の起床から夜の就寝まで、継続的な関係性の中でサービスを提供します。一方、コンシェルジュはホテルのロビーなどに常駐し、宿泊客や施設利用者のリクエストにその都度対応します。',
      },
      {
        type: 'h2',
        content: '求められるスキルの違い',
      },
      {
        type: 'p',
        content: '執事にはテーブルマナー、ワイン知識、衣類ケア、ハウスキーピングなど幅広い実務スキルが求められます。コンシェルジュは地域のレストラン、イベント、交通機関に関する幅広い情報網と、素早い手配能力が重視されます。',
      },
    ],
    faq: [
      {
        question: '執事とコンシェルジュ、どちらを目指すべきですか？',
        answer: '「特定の誰かのために長期的に尽くしたい」なら執事、「多様な顧客の課題を次々に解決したい」ならコンシェルジュが向いています。両方の要素を兼ね備えたハイブリッド職も増えています。',
      },
    ],
    relatedSlugs: ['butler-difference', 'butler-career-path'],
    ctaType: 'courses',
  },
  {
    slug: 'butler-demand-japan',
    title: '日本に執事は何人いる？富裕層市場と執事需要のいま',
    category: '業界・海外事情',
    categoryId: 'E',
    date: '2026-03-08',
    updated: '2026-06-13',
    author: '日本執事アカデミー講師',
    authorRole: '主任講師（全日本執事協会認定）',
    keywords: ['執事', '需要', '富裕層', '市場'],
    excerpt: '日本の富裕層市場の拡大に伴い、執事の需要は高まっているとされています。具体的な数字とともに現状を分析します。',
    definitionText: '日本国内でプロの執事として活動する人は推定200〜300名程度とされ、富裕層の増加とホスピタリティ産業の高度化に伴い、需要は供給を上回り続けています。',
    imageSeq: 'blog-demand-japan',
    imageUrl: '/images/generated/blog-demand-japan-01.jpg',
    toc: [
      { id: 'numbers', text: '日本の執事人口の推定' },
      { id: 'market', text: '拡大する富裕層市場' },
      { id: 'future', text: '今後の需要予測' },
    ],
    body: [
      {
        type: 'h2',
        content: '日本の執事人口の推定',
      },
      {
        type: 'p',
        content: '日本にプロの執事が何人いるか、正確な統計は存在しません。しかし、全日本執事協会の把握する範囲では、ホテルバトラー、個人邸宅の執事、法人向けコンサルタントなどを含めて200〜300名程度と推定されています。海外の主要な執事養成機関の修了生を含めても500名には満たないでしょう。',
      },
      {
        type: 'h2',
        content: '拡大する富裕層市場',
      },
      {
        type: 'p',
        content: '日本には約150万世帯の純金融資産1億円以上の富裕層がいるとされ（野村総合研究所 2024年推計）、この層が執事サービスの主な依頼主です。また、外資系高級ホテルの日本進出も続いており、バトラーサービスの需要は年々拡大しています。',
      },
    ],
    faq: [
      {
        question: '執事の求人はどこで見つけられますか？',
        answer: '全日本執事協会では修了生向けの求人情報提供の仕組みを準備しています。また、外資系高級ホテルの採用ページや、富裕層向け人材紹介会社もチャネルとなります。',
      },
    ],
    relatedSlugs: ['butler-career-path', 'butler-schools-worldwide'],
    ctaType: 'corporate',
  },
  {
    slug: 'butler-schools-worldwide',
    title: '世界の執事学校5選：オランダTIBAから英国BBIまで',
    category: '業界・海外事情',
    categoryId: 'E',
    date: '2026-02-15',
    updated: '2026-06-13',
    author: '日本執事アカデミー講師',
    authorRole: '主任講師（全日本執事協会認定）',
    keywords: ['執事', '学校', '海外', 'TIBA', 'BBI'],
    excerpt: '世界にはどのような執事養成機関があるのでしょうか。オランダ、英国、米国など主要な執事学校5校を紹介します。',
    definitionText: '世界の執事養成機関は、オランダのTIBA、英国のBritish Butler Instituteなど数校が国際的に知られており、いずれも数週間〜数ヶ月の集中プログラムを提供しています。',
    imageSeq: 'blog-schools-worldwide',
    imageUrl: '/images/generated/blog-schools-worldwide-01.jpg',
    toc: [
      { id: 'tiba', text: 'TIBA（オランダ）' },
      { id: 'bbi', text: 'British Butler Institute（英国）' },
      { id: 'compare', text: '日本執事アカデミーとの違い' },
    ],
    body: [
      {
        type: 'h2',
        content: 'TIBA（オランダ）',
      },
      {
        type: 'p',
        content: 'TIBA（The International Butler Academy）はオランダに本部を置く、世界で最も知られた執事養成機関のひとつです。約10週間の集中寮制プログラムで、世界中から受講生が集まります。卒業生は中東の王室や世界中の高級ホテルで活躍しています。',
      },
      {
        type: 'h2',
        content: 'British Butler Institute（英国）',
      },
      {
        type: 'p',
        content: '英国ロンドンに拠点を置くBBIは、伝統的な英国式バトラー養成を専門とします。短期集中コースから長期プログラムまで幅広く、特に英国貴族社会の伝統に根ざしたカリキュラムが特徴です。',
      },
    ],
    faq: [
      {
        question: '海外の執事学校と日本執事アカデミーの大きな違いは？',
        answer: '海外校は英語での授業、寮制、数週間の集中プログラムが一般的です。日本執事アカデミーは日本語で学べ、土日通学制で仕事と両立しやすい点が特徴です。また、日本の文化やおもてなしを強みとした独自カリキュラムを提供しています。',
      },
    ],
    relatedSlugs: ['butler-career-path', 'butler-qualification-guide'],
    ctaType: 'courses',
  },
];

// 自動生成記事(scripts/blog_auto/generate_blog.py が blogAuto.json に月次追記)を先頭にマージ。
// 新着が /blog 一覧の上位に来るよう auto を前に置く。
export const blogArticles: BlogArticle[] = [
  ...(blogAutoArticles as unknown as BlogArticle[]),
  ...manualBlogArticles,
];

export const blogAuthorInfo = {
  name: '日本執事アカデミー講師',
  role: '主任講師（全日本執事協会認定）',
  bio: '英国式執事教育と日本のおもてなし文化の両方に精通する現役の執事教育者。全日本執事協会が監修する教科書『E1 執事学入門：歴史・文化・職業倫理』の編著者の一人。',
};